import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  CircularProgress,
} from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Gallery from './Gallery';
import LoadErrorScreen from './LoadErrorScreen';
import CardTitleRow from './CardTitleRow';
import './ItemPage.css';

const getPhotoUrl = (photo) => {
  if (!photo || typeof photo !== 'string') return null;
  const trimmed = photo.trim();
  if (!trimmed) return null;
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  if (trimmed.startsWith('/')) return `${process.env.REACT_APP_SERVER_URL}${trimmed}`;
  return `${process.env.REACT_APP_SERVER_URL}/${trimmed}`;
};

const ServicePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchService = async () => {
      setLoading(true);
      setLoadError(false);
      setNotFound(false);
      try {
        const response = await fetch(`${process.env.REACT_APP_SERVER_URL}/api/services/public/${id}`);
        if (!response.ok) {
          if (response.status === 404) setNotFound(true);
          else setLoadError(true);
          setService(null);
          return;
        }
        const data = await response.json();
        setService(data);
      } catch (err) {
        console.error('Ошибка загрузки услуги:', err);
        setLoadError(true);
        setService(null);
      } finally {
        setLoading(false);
      }
    };
    fetchService();
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress sx={{ color: '#8B7D6B' }} />
      </Box>
    );
  }

  if (loadError) {
    return <LoadErrorScreen />;
  }

  if (notFound || !service) {
    return (
      <Container sx={{ py: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Услуга не найдена
        </Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/services')}
          sx={{ color: '#8B7D6B', mt: 2 }}
        >
          Назад к списку услуг
        </Button>
      </Container>
    );
  }

  const images = (service.photos || [])
    .map((p) => getPhotoUrl(p))
    .filter(Boolean);
  const phone = service.contactNumber ? String(service.contactNumber).trim() : '';
  const telHref = phone ? `tel:${phone.replace(/\s/g, '')}` : null;
  const locationText = [service.city, service.address].filter(Boolean).join(', ');

  return (
    <Container sx={{ paddingTop: 4, paddingBottom: 4, backgroundColor: '#F5F3F0', minHeight: '100vh' }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate('/services')}
        sx={{ color: '#8B7D6B', mb: 2 }}
      >
        Назад к списку услуг
      </Button>

      <Box className="item-detail-layout">
        <Box className="item-detail-gallery">
          {images.length > 0 ? (
            <Gallery images={images} />
          ) : (
            <Box
              sx={{
                width: '100%',
                height: 300,
                bgcolor: 'grey.200',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Typography color="text.secondary">Нет фото</Typography>
            </Box>
          )}
        </Box>

        <Box className="item-detail-content item-description" sx={{ mt: { xs: 3, md: 0 }, mb: 3 }}>
        <CardTitleRow
          type="service"
          entityId={service._id}
          titleVariant="h4"
          titleClassName="item-title"
          gutterBottom
          titleSx={{ color: '#000000', fontWeight: 'bold' }}
        >
          {service.name}
        </CardTitleRow>
        <Typography variant="h5" sx={{ mt: 2, mb: 2, color: '#8B7D6B', fontWeight: 'bold' }}>
          от {service.minPrice != null ? Number(service.minPrice).toLocaleString('ru-RU') : '—'} ₽
        </Typography>
        <Typography variant="body1" sx={{ color: '#000000', lineHeight: 1.8, mb: 3, whiteSpace: 'pre-wrap' }}>
          {service.description}
        </Typography>

        {locationText && (
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            📍 {locationText}
          </Typography>
        )}

        {telHref && (
          <Button
            component="a"
            href={telHref}
            variant="contained"
            size="large"
            startIcon={<PhoneIcon />}
            sx={{
              backgroundColor: '#2e7d32',
              '&:hover': { backgroundColor: '#1b5e20' },
            }}
          >
            Позвонить {phone && ` ${phone}`}
          </Button>
        )}
        </Box>
      </Box>
    </Container>
  );
};

export default ServicePage;
